import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useAppDispatch } from '@/store/hooks';
import { updateUser as updateReduxUser } from '@/store/slices/authSlice';
import { apiGet, apiPut, handleApiError } from '@/services/api';
import { User, UserEmail, UserPhone } from '@/types';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import ImageUpload from '@/components/ImageUpload';
import { 
  Save, 
  Plus, 
  X, 
  Mail, 
  Phone, 
  User as UserIcon, 
  MapPin, 
  Calendar,
  AlertCircle,
  CheckCircle,
  Trash2
} from 'lucide-react';

interface ProfileFormData {
  first_name: string;
  last_name: string;
  address: string;
  block_number: string;
  flat_number: string;
  date_of_birth: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  profile_picture: string;
  emails: UserEmail[];
  phones: UserPhone[];
}

const ProfilePage: React.FC = () => {
  const { user: currentUser, updateUser: updateZustandUser } = useAuthStore();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [profile, setProfile] = useState<User | null>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: '',
    last_name: '',
    address: '',
    block_number: '',
    flat_number: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    profile_picture: '',
    emails: [],
    phones: []
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet<{ success: boolean; data: User }>('/users/profile/me');
      const userData = (response as any).data ? (response as any).data : response;
      setProfile(userData);
      
      // Initialize form data
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        address: userData.address || '',
        block_number: userData.block_number || '',
        flat_number: userData.flat_number || '',
        date_of_birth: userData.date_of_birth ? userData.date_of_birth.split('T')[0] : '',
        emergency_contact_name: userData.emergency_contact_name || '',
        emergency_contact_phone: userData.emergency_contact_phone || '',
        profile_picture: userData.profile_picture || '',
        emails: userData.emails || [],
        phones: userData.phones || []
      });
    } catch (err: any) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddEmail = () => {
    setFormData(prev => ({
      ...prev,
      emails: [
        ...prev.emails,
        {
          id: `temp-${Date.now()}`,
          email: '',
          is_primary: prev.emails.length === 0,
          is_verified: false
        }
      ]
    }));
  };

  const handleRemoveEmail = (index: number) => {
    setFormData(prev => {
      const newEmails = prev.emails.filter((_, i) => i !== index);
      // Ensure at least one primary email if emails exist
      if (newEmails.length > 0 && !newEmails.some(e => e.is_primary)) {
        newEmails[0].is_primary = true;
      }
      return {
        ...prev,
        emails: newEmails
      };
    });
  };

  const handleEmailChange = (index: number, field: 'email' | 'is_primary', value: string | boolean) => {
    setFormData(prev => {
      const newEmails = [...prev.emails];
      if (field === 'is_primary' && value === true) {
        // Set all other emails to not primary
        newEmails.forEach((e, i) => {
          e.is_primary = i === index;
        });
      } else {
        newEmails[index] = {
          ...newEmails[index],
          [field]: value
        };
      }
      return {
        ...prev,
        emails: newEmails
      };
    });
  };

  const handleAddPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [
        ...prev.phones,
        {
          id: `temp-${Date.now()}`,
          phone: '',
          phone_type: 'mobile',
          is_primary: prev.phones.length === 0,
          is_verified: false
        }
      ]
    }));
  };

  const handleRemovePhone = (index: number) => {
    setFormData(prev => {
      const newPhones = prev.phones.filter((_, i) => i !== index);
      // Ensure at least one primary phone if phones exist
      if (newPhones.length > 0 && !newPhones.some(p => p.is_primary)) {
        newPhones[0].is_primary = true;
      }
      return {
        ...prev,
        phones: newPhones
      };
    });
  };

  const handlePhoneChange = (
    index: number, 
    field: 'phone' | 'phone_type' | 'is_primary', 
    value: string | boolean
  ) => {
    setFormData(prev => {
      const newPhones = [...prev.phones];
      if (field === 'is_primary' && value === true) {
        // Set all other phones to not primary
        newPhones.forEach((p, i) => {
          p.is_primary = i === index;
        });
      } else {
        newPhones[index] = {
          ...newPhones[index],
          [field]: value
        };
      }
      return {
        ...prev,
        phones: newPhones
      };
    });
  };

  const handleImageUploaded = (imageUrl: string) => {
    setFormData(prev => ({
      ...prev,
      profile_picture: imageUrl
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      // Validate emails
      if (formData.emails.length === 0) {
        throw new Error('At least one email address is required');
      }
      const invalidEmails = formData.emails.filter(e => !e.email || !e.email.includes('@'));
      if (invalidEmails.length > 0) {
        throw new Error('Please enter valid email addresses');
      }

      // Validate phones
      if (formData.phones.length === 0) {
        throw new Error('At least one phone number is required');
      }
      const invalidPhones = formData.phones.filter(p => !p.phone || p.phone.length < 10);
      if (invalidPhones.length > 0) {
        throw new Error('Please enter valid phone numbers');
      }

      const response = await apiPut<{ success: boolean; data: User; message: string }>(
        '/users/profile/me',
        {
          first_name: formData.first_name,
          last_name: formData.last_name,
          address: formData.address,
          block_number: formData.block_number,
          flat_number: formData.flat_number,
          date_of_birth: formData.date_of_birth || null,
          emergency_contact_name: formData.emergency_contact_name || null,
          emergency_contact_phone: formData.emergency_contact_phone || null,
          profile_picture: formData.profile_picture || null,
          emails: formData.emails.map(e => ({
            id: e.id.startsWith('temp-') ? undefined : e.id,
            email: e.email,
            is_primary: e.is_primary
          })),
          phones: formData.phones.map(p => ({
            id: p.id.startsWith('temp-') ? undefined : p.id,
            phone: p.phone,
            phone_type: p.phone_type,
            is_primary: p.is_primary
          }))
        }
      );

      const userData = (response as any).data || response;
      setProfile(userData);
      setFormData(prev => ({
        ...prev,
        emails: userData.emails || prev.emails,
        phones: userData.phones || prev.phones
      }));
      
      // Update both Zustand and Redux stores with new profile data (including profile picture)
      const updatedUserData = {
        profile_picture: userData.profile_picture || formData.profile_picture,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        phone: userData.phone,
        address: userData.address,
        block_number: userData.block_number,
        flat_number: userData.flat_number
      };
      
      console.log('Updating user stores with profile picture:', updatedUserData.profile_picture);
      
      // Update Zustand store
      updateZustandUser(updatedUserData);
      
      // Update Redux store
      dispatch(updateReduxUser(updatedUserData));
      
      // Force a small delay to ensure stores are updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <UserIcon className="w-6 h-6 mr-2" />
            My Profile
          </h1>
          {profile && (
            <div className="text-sm text-gray-500">
              Member ID: <span className="font-semibold">{profile.member_id}</span>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800">Profile updated successfully!</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Picture */}
          <div className="border-b pb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Picture</h2>
            <ImageUpload
              category="profile"
              onImageUploaded={handleImageUploaded}
              currentImageUrl={formData.profile_picture}
            />
          </div>

          {/* Basic Information */}
          <div className="border-b pb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="border-b pb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Address Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Block Number
                </label>
                <input
                  type="text"
                  name="block_number"
                  value={formData.block_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Flat Number
                </label>
                <input
                  type="text"
                  name="flat_number"
                  value={formData.flat_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Email Addresses */}
          <div className="border-b pb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Email Addresses
              </h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddEmail}
                icon={<Plus className="w-4 h-4" />}
              >
                Add Email
              </Button>
            </div>
            <div className="space-y-3">
              {formData.emails.map((email, index) => (
                <div key={email.id || index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <input
                      type="email"
                      value={email.email}
                      onChange={(e) => handleEmailChange(index, 'email', e.target.value)}
                      placeholder="Enter email address"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={email.is_primary}
                        onChange={(e) => handleEmailChange(index, 'is_primary', e.target.checked)}
                        className="mr-1"
                      />
                      Primary
                    </label>
                    {formData.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {formData.emails.length === 0 && (
                <p className="text-sm text-gray-500">No email addresses added. Click "Add Email" to add one.</p>
              )}
            </div>
          </div>

          {/* Phone Numbers */}
          <div className="border-b pb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Phone className="w-5 h-5 mr-2" />
                Phone Numbers
              </h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddPhone}
                icon={<Plus className="w-4 h-4" />}
              >
                Add Phone
              </Button>
            </div>
            <div className="space-y-3">
              {formData.phones.map((phone, index) => (
                <div key={phone.id || index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="tel"
                      value={phone.phone}
                      onChange={(e) => handlePhoneChange(index, 'phone', e.target.value)}
                      placeholder="Enter phone number"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <select
                      value={phone.phone_type}
                      onChange={(e) => handlePhoneChange(index, 'phone_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="mobile">Mobile</option>
                      <option value="landline">Landline</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="emergency">Emergency</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={phone.is_primary}
                        onChange={(e) => handlePhoneChange(index, 'is_primary', e.target.checked)}
                        className="mr-1"
                      />
                      Primary
                    </label>
                    {formData.phones.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemovePhone(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {formData.phones.length === 0 && (
                <p className="text-sm text-gray-500">No phone numbers added. Click "Add Phone" to add one.</p>
              )}
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="border-b pb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={fetchProfile}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={saving}
              icon={<Save className="w-4 h-4" />}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
